import { useContext, useState } from "react";
import { Button, Input, Select } from "../../../ui";
import { Context } from "../../../../context/Context";
import { hasValidSarPrecision } from "../../../../utils/billingMoney";
import { optionsFromLabels, useBillingAdminText } from "../billingAdminI18n";

const defaults = {
  code: "",
  nameAr: "",
  nameEn: "",
  descriptionAr: "",
  descriptionEn: "",
  type: "platform",
  highlighted: false,
  billingCycle: "monthly",
  basePrice: "",
  extraBranchPrice: "0",
  includedBranchCount: 1,
  featuresText: "",
  effectiveFrom: "",
  status: "active",
};

const PlanForm = ({
  initialValues = {},
  includeCode = false,
  submitLabel,
  onSubmit,
  onCancel,
}) => {
  const { lang } = useContext(Context);
  const text = useBillingAdminText(lang);
  const [values, setValues] = useState({ ...defaults, ...initialValues });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const update = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));

  const validate = () => {
    if (includeCode && !values.code.trim()) return text.plans.codeRequired;
    if (!values.nameAr.trim() || !values.nameEn.trim())
      return text.plans.namesRequired;
    if (!hasValidSarPrecision(values.basePrice))
      return text.plans.basePricePrecision;
    if (!hasValidSarPrecision(values.extraBranchPrice))
      return text.plans.extraBranchPricePrecision;
    if (Number(values.includedBranchCount) < 0)
      return text.plans.includedBranchCountInvalid;
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit({
        ...(includeCode ? { code: values.code } : {}),
        nameAr: values.nameAr,
        nameEn: values.nameEn,
        descriptionAr: values.descriptionAr,
        descriptionEn: values.descriptionEn,
        type: values.type,
        highlighted: values.highlighted,
        billingCycle: values.billingCycle,
        basePrice: values.basePrice,
        extraBranchPrice: values.extraBranchPrice,
        includedBranchCount: Number(values.includedBranchCount),
        features: values.featuresText
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean),
        effectiveFrom: values.effectiveFrom || null,
        status: values.status,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form className="billing-plan-modal-form" onSubmit={submit}>
      {error && <div className="billing-error">{error}</div>}
      {includeCode && (
        <Input
          label={text.plans.code}
          value={values.code}
          onChange={(event) => update("code", event.target.value)}
          required
        />
      )}
      <div className="billing-form-grid">
        <Input
          label={text.plans.arabicName}
          value={values.nameAr}
          onChange={(event) => update("nameAr", event.target.value)}
          required
        />
        <Input
          label={text.plans.englishName}
          value={values.nameEn}
          onChange={(event) => update("nameEn", event.target.value)}
          required
        />
      </div>
      <div className="billing-form-grid">
        <Select
          label={text.plans.packageType}
          options={optionsFromLabels(text.types, ["platform", "integration"])}
          value={values.type}
          onChange={(event) => update("type", event.target.value)}
        />
        <Select
          label={text.common.status}
          options={optionsFromLabels(text.statuses, ["active", "draft"])}
          value={values.status}
          onChange={(event) => update("status", event.target.value)}
        />
      </div>
      <div className="billing-form-grid">
        <Select
          label={text.plans.cycle}
          options={optionsFromLabels(text.cycles, [
            "monthly",
            "quarterly",
            "yearly",
          ])}
          value={values.billingCycle}
          onChange={(event) => update("billingCycle", event.target.value)}
        />
      </div>
      <label className="billing-inline-toggle-row">
        <input
          type="checkbox"
          checked={values.highlighted}
          onChange={(event) => update("highlighted", event.target.checked)}
        />
        {text.plans.highlighted}
      </label>
      <div className="billing-form-grid">
        <Input
          label={text.plans.basePriceSar}
          value={values.basePrice}
          onChange={(event) => update("basePrice", event.target.value)}
          required
        />
        <Input
          label={text.plans.extraBranchPriceSar}
          value={values.extraBranchPrice}
          onChange={(event) => update("extraBranchPrice", event.target.value)}
          required
        />
      </div>
      <div className="billing-form-grid">
        <Input
          type="number"
          min="0"
          label={text.plans.includedBranches}
          value={values.includedBranchCount}
          onChange={(event) =>
            update("includedBranchCount", event.target.value)
          }
        />
        <Input
          type="date"
          label={text.plans.effectiveFrom}
          value={values.effectiveFrom}
          onChange={(event) => update("effectiveFrom", event.target.value)}
        />
      </div>
      <Input
        textarea
        label={text.plans.arabicDescription}
        value={values.descriptionAr}
        onChange={(event) => update("descriptionAr", event.target.value)}
      />
      <Input
        textarea
        label={text.plans.englishDescription}
        value={values.descriptionEn}
        onChange={(event) => update("descriptionEn", event.target.value)}
      />
      <Input
        textarea
        label={text.plans.features}
        helperText={text.plans.oneFeaturePerLine}
        value={values.featuresText}
        onChange={(event) => update("featuresText", event.target.value)}
      />
      <div className="billing-modal-actions">
        <Button type="submit" appearance="filled" loading={saving}>
          {submitLabel}
        </Button>
        <Button appearance="ghost" onClick={onCancel}>
          {text.common.cancel}
        </Button>
      </div>
    </form>
  );
};

export default PlanForm;
